import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProgressBar, LearnerStatusBadge } from "./learnerShell";
import { learnerPath } from "./_learnerConfig";

const objectValue = (...values) =>
  values.find((value) => value && typeof value === "object");

const stringValue = (...values) =>
  values.find((value) => typeof value === "string" && value.trim());

const numberValue = (...values) => {
  const match = values.find(
    (value) => value !== undefined && value !== null && value !== "" && !Number.isNaN(Number(value))
  );
  return match === undefined ? null : Number(match);
};

const usableImageUrl = (...values) => {
  const value = stringValue(...values);
  if (!value) return null;
  return /^(https?:|data:|blob:|\/)/i.test(value) ? value : null;
};

export const isMandatoryCourse = (row = {}) =>
  /mandatory|required/i.test(String(row?.category || ""));

export const formatCourseDuration = (seconds) => {
  const totalSeconds = Math.max(0, Number(seconds) || 0);
  if (!totalSeconds) return "";
  const totalMinutes = Math.round(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hLabel = hours === 1 ? "Hour" : "Hours";
  const mLabel = minutes === 1 ? "Minute" : "Minutes";
  if (hours && minutes) return `${hours} ${hLabel} ${minutes} ${mLabel}`;
  if (hours) return `${hours} ${hLabel}`;
  return `${minutes} ${mLabel}`;
};

const normaliseContentSummary = (summary = {}) => ({
  modules: Math.max(0, numberValue(summary?.modules) || 0),
  parts: Math.max(0, numberValue(summary?.parts) || 0),
  quizzes: Math.max(0, numberValue(summary?.quizzes) || 0),
  videoDurationSeconds: Math.max(0, numberValue(summary?.videoDurationSeconds) || 0),
});

// Normalise one catalogue/enrolment row into what the card needs. The API may
// hand us a course with an embedded enrolment (GET /courses) or an enrolment
// with an embedded course (GET /enrolments) — handle both defensively.
export const normalizeCourseRow = (row) => {
  if (
    row &&
    row.courseId &&
    !row.course &&
    !row.enrolment &&
    !row.courseVersion &&
    !row.courseVersionRef &&
    !row.courseSnapshot &&
    typeof row.courseId === "string"
  ) {
    return {
      ...row,
      contentSummary: normaliseContentSummary(row?.contentSummary),
    };
  }

  const course =
    objectValue(
      row?.course,
      row?.courseId,
      row?.courseVersion,
      row?.courseVersionRef,
      row?.courseSnapshot,
      row?.snapshot?.course
    ) || row;

  const enrolment =
    row?.enrolment && typeof row.enrolment === "object"
      ? row.enrolment
      : objectValue(
            row?.course,
            row?.courseId,
            row?.courseVersion,
            row?.courseVersionRef,
            row?.courseSnapshot
          )
        ? row
        : null;

  const status =
    enrolment?.status || row?.enrolmentStatus || row?.completionStatus || null;

  return {
    courseId:
      course?._id ||
      (typeof course?.courseId === "string" ? course.courseId : course?.courseId?._id) ||
      (typeof row?.courseId === "string" ? row.courseId : row?.courseId?._id),
    slug: course?.slug || row?.slug || "",
    title: course?.title || "",
    description: course?.description || "",
    category: stringValue(course?.category, row?.category) || "",
    thumbnailUrl: usableImageUrl(
      course?.thumbnailUrl,
      course?.thumbnailSignedUrl,
      course?.signedThumbnailUrl,
      course?.thumbnailURL,
      course?.imageUrl,
      course?.coverImageUrl,
      course?.coverUrl,
      course?.thumbnail?.url,
      course?.thumbnail?.signedUrl,
      row?.thumbnailUrl
    ),
    enrolled:
      (!!status && status !== "not_enrolled") ||
      !!row?.enrolled ||
      !!row?.isEnrolled,
    status,
    progressPercent: enrolment?.progressPercent ?? row?.progressPercent ?? null,
    enrolmentId: enrolment?._id || enrolment?.enrolmentId || null,
    contentSummary: normaliseContentSummary(course?.contentSummary || row?.contentSummary),
    totalEnrolments: Math.max(0, numberValue(course?.totalEnrolments, row?.totalEnrolments) || 0),
    createdAt: course?.createdAt || row?.createdAt || null,
    lastActivity:
      enrolment?.lastActivity ||
      enrolment?.updatedAt ||
      row?.lastActivity ||
      row?.updatedAt ||
      enrolment?.enrolledAt ||
      row?.enrolledAt ||
      null,
  };
};

const LearnerCourseCard = ({ row, onEnrol, enrolling }) => {
  const { orgName } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const c = normalizeCourseRow(row);
  const completed = (c.status || "").toLowerCase() === "completed";
  const mandatory = isMandatoryCourse(c);

  const routeCourseId = encodeURIComponent(c.slug || c.courseId || "");
  const open = () => {
    if (!routeCourseId) return;
    navigate(learnerPath(orgName, "course/" + routeCourseId));
  };
  const openLearn = () => {
    if (!routeCourseId) return;
    navigate(learnerPath(orgName, "course/" + routeCourseId + "/player"));
  };


  return (
    <div className="lms-course-card">
      <button
        type="button"
        className="lms-course-card-thumb"
        onClick={open}
        aria-label={t("learner.lms.course.viewNamed", "View {{title}}", {
          title: c.title || t("learner.lms.course.untitled", "Course"),
        })}
      >
        {c.thumbnailUrl ? (
          <img src={c.thumbnailUrl} alt="" />
        ) : (
          <i className="fas fa-book-open" aria-hidden="true"></i>
        )}
      </button>
      <div className="lms-course-card-body">
        <div className="lms-course-card-top">
          <div className="lms-course-card-titlewrap">
            {c.category ? (
              <span className="lms-course-card-eyebrow">{c.category}</span>
            ) : null}
            <h3 className="lms-course-card-title">
              <button type="button" className="lms-course-card-title-button" onClick={open}>
                {c.title || t("learner.lms.course.untitled", "Course")}
              </button>
            </h3>
          </div>
          <div className="lms-course-card-tags">
            {mandatory ? (
              <span className="lms-portal-badge blue">
                {t("learner.lms.course.mandatory", "Mandatory")}
              </span>
            ) : null}
            {c.enrolled ? <LearnerStatusBadge status={c.status} t={t} /> : null}
          </div>
        </div>


        {c.description ? <p className="lms-course-card-desc">{c.description}</p> : null}

        {c.enrolled && c.progressPercent != null ? (
          <div className="lms-course-card-progresswrap">
            <div className="lms-course-card-progresshead">
              <span>{t("learner.lms.course.progress", "Progress")}</span>
              <span className="lms-course-card-pct">{Math.round(c.progressPercent)}%</span>
            </div>
            <div className="lms-course-card-progress">
              <ProgressBar percent={c.progressPercent} small />
            </div>
          </div>
        ) : null}

        <div className="lms-course-card-actions">
          {c.enrolled ? (
            <button className="btn lms-btn-primary" onClick={openLearn}>
              {completed
                ? t("learner.lms.course.review", "Review course")
                : t("learner.lms.course.continue", "Continue")}
            </button>
          ) : null}

          <button className={
            "btn " + (c.enrolled ? "lms-btn-ghost" : "lms-btn-primary")
          } onClick={open}>
            {c.enrolled
              ? t("learner.lms.course.view", "View details")
              : t("learner.lms.course.view", "View")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearnerCourseCard;
